// Voice recorder UI for the Rep PWA. Mobile-first: single big mic button,
// live amplitude bar (visualizer), interim transcript display, stop/cancel.

import { Mic, Square, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { type FinalResult, VoiceRecorder } from '../lib/voice/recorder';

type State =
  | { kind: 'idle' }
  | { kind: 'recording'; sessionId: string | null }
  | { kind: 'processing' }
  | { kind: 'done'; result: FinalResult }
  | { kind: 'error'; message: string };

interface Props {
  onComplete?: (result: FinalResult) => void;
  /** Optional callback so a parent (e.g. P6 intent extraction) can react to the transcript. */
  onTranscriptReady?: (transcript: string, sessionId: string) => void;
}

const VISUALIZER_BARS = 24;

export function VoiceRecorderUI({ onComplete, onTranscriptReady }: Props) {
  const [state, setState] = useState<State>({ kind: 'idle' });
  const [interim, setInterim] = useState('');
  const [final, setFinal] = useState('');
  const [bars, setBars] = useState<number[]>(() => new Array(VISUALIZER_BARS).fill(0));
  const recorderRef = useRef<VoiceRecorder | null>(null);
  const barsRef = useRef<number[]>(new Array(VISUALIZER_BARS).fill(0));

  useEffect(() => () => recorderRef.current?.cancel(), []);

  const handleStart = async () => {
    setInterim('');
    setFinal('');
    setBars(new Array(VISUALIZER_BARS).fill(0));
    barsRef.current = new Array(VISUALIZER_BARS).fill(0);

    const recorder = new VoiceRecorder({
      mode: 'streaming',
      purpose: 'quote',
      onEvent: (event) => {
        if (event.type === 'open') {
          setState({ kind: 'recording', sessionId: event.sessionId });
        } else if (event.type === 'interim') {
          if (event.isFinal) {
            setFinal((prev) => `${prev} ${event.text}`.trim());
            setInterim('');
          } else {
            setInterim(event.text);
          }
        } else if (event.type === 'final') {
          setFinal(event.transcript);
          setInterim('');
        } else if (event.type === 'error') {
          setState({ kind: 'error', message: event.message });
        }
      },
      onLevel: (level) => {
        // Push level onto a rolling buffer for the visualizer.
        const next = barsRef.current.slice(1);
        next.push(level);
        barsRef.current = next;
        setBars(next);
      },
    });
    recorderRef.current = recorder;

    try {
      await recorder.start();
      setState({ kind: 'recording', sessionId: null });
    } catch (err) {
      const msg = (err as Error).message;
      // Friendlier copy for the most common case.
      const friendly =
        msg.includes('Permission') || msg.includes('NotAllowed')
          ? 'Microphone access denied. Enable it in your browser/device settings.'
          : msg;
      setState({ kind: 'error', message: friendly });
    }
  };

  const handleStop = async () => {
    if (!recorderRef.current) return;
    setState({ kind: 'processing' });
    try {
      const result = await recorderRef.current.stop();
      setState({ kind: 'done', result });
      setFinal(result.transcript);
      onComplete?.(result);
      onTranscriptReady?.(result.transcript, result.sessionId);
    } catch (err) {
      setState({ kind: 'error', message: (err as Error).message });
    } finally {
      recorderRef.current = null;
    }
  };

  const handleCancel = () => {
    recorderRef.current?.cancel();
    recorderRef.current = null;
    setState({ kind: 'idle' });
    setInterim('');
    setFinal('');
  };

  const isRecording = state.kind === 'recording';
  const isProcessing = state.kind === 'processing';

  return (
    <div className="flex flex-col items-center gap-6 px-4 py-6">
      <div
        aria-label="audio levels"
        className="flex h-16 w-full max-w-sm items-center justify-center gap-1"
      >
        {bars.map((level, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: visualizer position is the key
            key={i}
            className="w-2 rounded-full bg-primary transition-[height] duration-50"
            style={{
              height: `${Math.max(8, Math.round(level * 100))}%`,
              opacity: isRecording ? 1 : 0.25,
            }}
          />
        ))}
      </div>

      <button
        type="button"
        aria-label={isRecording ? 'Stop recording' : 'Start recording'}
        onClick={isRecording ? handleStop : handleStart}
        disabled={isProcessing}
        className={`flex h-32 w-32 items-center justify-center rounded-full text-white shadow-lg transition-all active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 ${
          isRecording ? 'bg-error animate-pulse' : 'bg-accent hover:bg-accent-hover'
        }`}
      >
        {isRecording ? <Square size={56} /> : <Mic size={56} />}
      </button>

      {isRecording && (
        <button
          type="button"
          onClick={handleCancel}
          className="flex items-center gap-1 text-sm text-text-muted hover:text-error"
          aria-label="Cancel"
        >
          <X size={16} /> Cancel
        </button>
      )}

      <div className="min-h-[120px] w-full max-w-md rounded-lg border border-border bg-surface p-4">
        {state.kind === 'idle' && (
          <p className="text-center text-sm text-text-muted">
            Tap the mic to start. Try:{' '}
            <em>"Quote for Miller Construction, fifty 2x4x10 SPF studs"</em>
          </p>
        )}
        {state.kind === 'error' && (
          <p className="text-center text-sm text-error">{state.message}</p>
        )}
        {(state.kind === 'recording' || state.kind === 'done' || state.kind === 'processing') && (
          <div className="space-y-2 text-sm">
            {final && <p className="text-text">{final}</p>}
            {interim && (
              <p className="text-text-muted italic">
                <span className="opacity-70">…</span> {interim}
              </p>
            )}
            {state.kind === 'processing' && (
              <p className="text-center text-xs text-text-muted">Finalising transcript…</p>
            )}
            {state.kind === 'done' && (
              <p className="text-center text-xs text-success">
                ✓ saved to session {state.result.sessionId.slice(0, 8)}…
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
